import { supabase } from '@/integrations/supabase/client';

export type ResilienceOptions = {
  retries?: number;
  retryDelayBaseMs?: number;
  timeoutMs?: number;
  breakerKey?: string;
  breakerThreshold?: number;
  breakerCooldownMs?: number;
  skipRetry?: boolean;
  /** External AbortSignal — when aborted, in-flight request is cancelled and no further retries happen. */
  signal?: AbortSignal;
};

type BreakerState = { failures: number; openUntil: number };
const breakers = new Map<string, BreakerState>();

const now = () => Date.now();

function isBreakerOpen(key: string | undefined) {
  if (!key) return false;
  const b = breakers.get(key);
  return !!b && b.openUntil > now();
}

function recordFailure(key: string | undefined, threshold: number, cooldownMs: number) {
  if (!key) return;
  const b = breakers.get(key) ?? { failures: 0, openUntil: 0 };
  b.failures += 1;
  if (b.failures >= threshold) {
    b.openUntil = now() + cooldownMs;
    b.failures = 0;
    console.warn(`[CircuitBreaker] Opened for key="${key}" for ${cooldownMs}ms`);
  }
  breakers.set(key, b);
}

function recordSuccess(key: string | undefined) {
  if (!key) return;
  breakers.set(key, { failures: 0, openUntil: 0 });
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function withTimeout<T>(p: Promise<T>, timeoutMs: number, controller: AbortController): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        controller.abort();
        reject(new Error(`Request timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);
}

function mapErrorToUserMessage(err: any): string {
  const msg = typeof err?.message === 'string' ? err.message : String(err ?? 'Unknown error');
  if (/429|rate limit/i.test(msg)) return 'Too many requests right now. Please wait a moment and try again.';
  if (/403|forbidden|not_allowed/i.test(msg)) return 'Access denied. Please try again or contact support.';
  if (/5\d\d|server|unavailable|timed out/i.test(msg)) return 'Service is temporarily unavailable. Please try again shortly.';
  if (/network|fetch|failed/i.test(msg)) return 'Network error. Check your connection and try again.';
  if (/401|unauthorized|jwt|expired/i.test(msg)) return 'Session expired. Please sign in again.';
  return 'Something went wrong. Please try again.';
}

function is401Error(err: any): boolean {
  if (!err) return false;
  const msg = typeof err?.message === 'string' ? err.message : '';
  const status = err?.status ?? err?.statusCode ?? 0;
  return status === 401 || /401|unauthorized|jwt.*expired/i.test(msg);
}

export async function withResilience<T>(
  doRequest: (signal: AbortSignal) => Promise<T>,
  options: ResilienceOptions = {}
): Promise<{ data?: T; error?: Error; userMessage?: string }> {
  const {
    retries = 2,
    // Default raised to 30s to prevent silent 15s cutoffs on slow connections/cold starts
    retryDelayBaseMs = 400,
    timeoutMs = 30000,
    breakerKey = 'ratehawk',
    breakerThreshold = 4,
    breakerCooldownMs = 60000,
    skipRetry = false,
    signal: externalSignal,
  } = options;

  if (isBreakerOpen(breakerKey)) {
    const e = new Error('Circuit breaker open; skipping request.');
    return { error: e, userMessage: 'Service is temporarily unavailable. Please try again shortly.' };
  }

  // Bail out immediately if external signal is already aborted.
  if (externalSignal?.aborted) {
    const e = new Error('Aborted');
    e.name = 'AbortError';
    return { error: e };
  }

  const effectiveRetries = skipRetry ? 0 : retries;
  let attempt = 0;
  let triedSessionRefresh = false;

  while (attempt <= effectiveRetries) {
    const controller = new AbortController();
    // Forward external aborts to the per-attempt controller.
    const onExternalAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    try {
      const result = await withTimeout(doRequest(controller.signal), timeoutMs, controller);
      recordSuccess(breakerKey);
      return { data: result };
    } catch (err: any) {
      // External-abort short circuit — don't retry, don't record breaker failure.
      if (externalSignal?.aborted || err?.name === 'AbortError') {
        const e = err instanceof Error ? err : new Error('Aborted');
        if (!(e as any).name) (e as any).name = 'AbortError';
        return { error: e };
      }

      // 401 recovery: refresh session and retry once (regardless of skipRetry)
      if (is401Error(err) && !triedSessionRefresh) {
        triedSessionRefresh = true;
        try {
          await supabase.auth.getSession();
        } catch { /* ignore refresh errors */ }
        // Retry the request once after session refresh
        continue;
      }

      attempt += 1;

      const isLast = attempt > effectiveRetries;
      if (isLast) {
        recordFailure(breakerKey, breakerThreshold, breakerCooldownMs);
        return { error: err, userMessage: mapErrorToUserMessage(err) };
      }

      const backoff = retryDelayBaseMs * Math.pow(2, attempt - 1);
      const jitter = Math.floor(Math.random() * 150);
      await sleep(backoff + jitter);
    } finally {
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
    }
  }
  return { error: new Error('Unknown error after retries') };
}

export async function callFunctionWithResilience<T = any>(
  functionName: string,
  body: any,
  options?: ResilienceOptions
): Promise<{ data?: T; error?: Error; userMessage?: string }> {
  return withResilience<T>(
    async (signal) => {
      const { data, error } = await supabase.functions.invoke(functionName, { body, signal });
      if (error) throw error;
      if (data === null || data === undefined) throw new Error('No data returned from ' + functionName);
      // Handle 202 pending status from backend (search coalescing)
      const parsed = data as any;
      if (parsed?.status === 'pending') {
        const pendingErr = new Error('search_pending');
        (pendingErr as any).isPending = true;
        throw pendingErr;
      }
      return data as T;
    },
    { breakerKey: functionName, ...options }
  );
}

/**
 * Fire-and-forget edge function call. Never throws, logs errors silently.
 * Use for telemetry, notifications, emails — anything non-critical.
 */
export async function invokeFireAndForget(
  functionName: string,
  body: any
): Promise<void> {
  try {
    await supabase.functions.invoke(functionName, { body });
  } catch (err) {
    console.warn(`[fireAndForget] ${functionName} failed:`, err);
  }
}
